import _ from 'lodash';
import React, { useState, useEffect, useContext, useRef } from 'react';
import moment from 'moment';
import { Card, Badge } from 'react-bootstrap';
import ReactTable from 'react-table';
import ModalContext from './ModalContext';
import ApiService from '../../services/ApiService';
import Form from '../form/Form';
import LoadingPage from '../LoadingPage';

const columns = [
  {
    Header: 'Date',
    accessor: 'date',
    width: 170,
    Cell: (c: any) => <span>{new Date(c.row.date).toLocaleString()}</span>,
  },
  { Header: 'Initials', accessor: 'ppc', width: 65 },
  {
    Header: 'Note',
    accessor: 'note',
    Cell: (c: any) => c.row.note.split('\n').map((line: string) => <div>{line}</div>),
  },
];

interface Contact {
  name: string;
  email: string;
  phone: string;
}

interface ContactLabel {
  label: string;
}

interface Props {
  lookupKey: string;
  view: string;
  meta?: any;
}

const ModalRepairNotes = ({ lookupKey = '', view = 'repairNotes', meta }: Props) => {
  const formRef = useRef<any>(null);

  const [data, setData] = useState([]);
  const [smsContacts, setSmsContacts] = useState<ContactLabel[] | null>(null);
  const [emailContacts, setEmailContacts] = useState<ContactLabel[] | null>(null);
  const [repairNoteOptions, setRepairNoteOptions] = useState(null);

  const { setTitle }: any = useContext(ModalContext);

  const layout = [
    {
      type: 'string',
      name: 'repairId',
      data: lookupKey,
      hidden: true,
    },
    {
      name: 'repairNoteOptions',
      label: 'Canned Response',
      options: repairNoteOptions,
    },
    {
      name: 'repairNote',
      label: 'Custom Repair Note',
      type: 'string',
      placeholder: 'Enter repair note details',
      required: true,
      data: '',
      control: 'Textarea',
    },
    {
      name: 'smsUsers',
      label: 'Text (SMS) Users',
      control: 'Tags',
      validation: {
        modelName: 'User',
        type: 'db',
        filterSuggestions: (suggestions: any) => suggestions.filter((x: any) => x.phone),
      },
      data: [],
      collection: true,
      helpText: 'Only users with phone number set are selectable',
      col: 6,
    },
    {
      name: 'smsContacts',
      label: 'Text (SMS) Customer',
      control: 'Tags',
      validation: {
        type: 'static',
        suggestions: smsContacts,
        optional: true,
        allowSelectAll: true,
        filterBy: 'label',
      },
      data: [],
      collection: true,
      helpText: 'Numbers come from customer contacts for selected RO',
      col: 6,
    },
    {
      name: 'emailUsers',
      label: 'Email Users',
      control: 'Tags',
      validation: {
        modelName: 'User',
        type: 'db',
        filterSuggestions: (suggestions: any) => suggestions.filter((x: any) => x.email),
      },
      data: [],
      collection: true,
      col: 6,
    },
    {
      name: 'emailContacts',
      label: 'Email Customer',
      control: 'Tags',
      validation: {
        type: 'static',
        suggestions: emailContacts,
        optional: true,
        allowSelectAll: true,
        filterBy: 'label',
      },
      data: [],
      collection: true,
      helpText: 'Emails come from customer contacts for selected RO',
      col: 6,
    },
  ];

  const refreshList = () => {
    ApiService.get(`repair/${encodeURIComponent(lookupKey)}/customer`).then(response => {
      const contacts: Contact[] = _.get(response.data, 'contacts');

      if (!contacts || !contacts.length) {
        setEmailContacts([]);
        setSmsContacts([]);
        return;
      }

      setEmailContacts(
        (contacts.filter(x => x.email) || []).map(contact => {
          return { label: `${contact.name} - ${contact.email}` };
        })
      );

      setSmsContacts(
        (contacts.filter(x => x.phone) || []).map(contact => {
          return { label: `${contact.name} - ${contact.phone}` };
        })
      );
    });

    ApiService.get(`drilldown/${view}/${encodeURIComponent(lookupKey)}`).then(response => {
      const newData = response.data.data;

      // TODO Should be done at a much lower level
      if (typeof newData === 'string') {
        setData(JSON.parse(newData));
      } else {
        setData(newData);
      }
    });
  };

  useEffect(() => {
    refreshList();
    ApiService.get('list/STB18', { params: { skipSearch: 1 } }).then(({ data }) => {
      setRepairNoteOptions(
        data.list.map((item: any) => {
          return { label: `${item.itemid}: ${item.Description}`, value: item.Description };
        })
      );
    });
    setTitle(`Repair Notes (${lookupKey})`);
  }, []);

  const addNote = (payload: any) => {
    const params: any = {};
    payload.forEach((param: any) => {
      params[param.name] = param.data;
    });

    params.vin = _.get(meta, 'rowData.vinNo');

    return ApiService.put(`repair/${encodeURIComponent(lookupKey)}/note`, params);
  };

  const renderTitle = () => (
    <p>
      Repair Notes <Badge>{data.length}</Badge>
    </p>
  );

  const renderNotes = () => {
    if (!data.length) {
      return (
        <div className="loading-wrapper">
          <div className="loading-child">Empty</div>
        </div>
      );
    }

    const formatData = () => {
      return data.map((note: any) => {
        return {
          ...note,
          date: moment(`${note.date} ${note.time}`, 'MM-DD-YYYY HH:mm:ss').valueOf(),
        };
      });
    };

    const smallView = () => {
      return data.map((note: any) => (
        <div className="mb-2" style={{ borderBottom: '1px solid #ddd' }}>
          <div>
            {note.date} - {note.ppc}
          </div>
          <div>{note.note}</div>
        </div>
      ));
    };

    return (
      <>
        <div className="d-none d-sm-block">
          <ReactTable
            className="-striped -highlight"
            columns={columns}
            pageSize={5}
            data={formatData()}
            showPagination={true}
            sortable
            style={{
              height: '520px',
              overflow: 'auto',
            }}
          />
        </div>
        <div className="d-block d-sm-none">{smallView()}</div>
      </>
    );
  };

  const handleChange = ({ name, value, form }: any) => {
    if (name === 'repairNoteOptions') {
      formRef.current.updateForm({ repairNote: `${value}\n${form.repairNote.data || ''}` });
    }
  };

  if (!smsContacts || !repairNoteOptions) {
    return <LoadingPage />;
  }

  return (
    <div className="modal-repair-notes">
      <Card className="mb-3">
        <Card.Header>
          <Card.Title>Add Repair Note</Card.Title>
        </Card.Header>
        <Card.Body>
          <Form
            feedback="none"
            hideCancel
            layout={layout}
            onChange={handleChange}
            ref={formRef}
            resetOnSubmit
            showHelp
            submit={addNote}
            submitCallback={refreshList}
          />
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header>
          <Card.Title>{renderTitle()}</Card.Title>
        </Card.Header>
        <Card.Body>{renderNotes()}</Card.Body>
      </Card>
    </div>
  );
};

export default ModalRepairNotes;
